import ContentCreator from "@/views/content/contentCreator/ContentCreator.vue";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import ContentCreatorDlg from "@/views/content/contentCreatorDlg/ContentCreatorDlg.vue";
import Loader from "@/components/loader/Loader.vue";
import {ContentStatus} from "@/enums/ContentStatus";
import moment from "moment";
import html2canvas from "html2canvas";
import VueHtml2pdf from 'vue-html2pdf';
import {CustomContentMixin} from "@/mixins/CustomContent";
import imageToBase64 from 'image-to-base64/browser';
import {ContentTypes} from "@/enums/ContentTypes";
import {EventLocations} from "@/enums/EventLocations";
import {optimizePhotos, uploadImageAsync} from "@/utils/upload-image";
import {v4 as uuidv4} from "uuid";
import {DEFAULT_NO_IMAGE} from "@/consts/default-no-image";
import {OrientationTypes} from "@/enums/OrientationTypes";
import ContentEditDlg from "@/views/content/contentEditDlg/ContentEditDlg.vue";
import ContentCreatorPublishDlg from "@/views/content/contentCreatorPublishDlg/ContentCreatorPublishDlg.vue";
import ContentTagsDlg from "@/views/content/contentTagsDlg/ContentTagsDlg.vue";
import {ContentCreatorTypes} from "@/enums/ContentCreatorTypes";
import {delay} from "@/utils/wait-resized-image";

export default {
    name: "ContentView",
    components: {
        ContentCreator,
        ConfirmDlg,
        Loader,
        VueHtml2pdf,
        ContentCreatorDlg,
        ContentEditDlg,
        ContentCreatorPublishDlg,
        ContentTagsDlg
    },
    mixins: [CustomContentMixin],
    inject: ['isSidebarShow'],
    filters: {
        capitalize(value) {
            if (!value) return "";
            return value.charAt(0).toUpperCase() + value.slice(1);
        },
    },
    data() {
        return {
            builder: null,
            builderData: {},
            isNavShow: this.isSidebarShow,
            contentScreenshot: null,
            contentScreenshotResult: null,
            contentPDF: null,
            isShowWings: false,
            selectedWing: {
                id: 'All',
                name: this.$t('Calendar.AllWings')
            },
            isDisablingSaving: true,
            loading: {
                save: false,
                publish: false,
                delete: false,
                page: true,
                export: false,
            },
            selectedOrientation: '',
            isLandscape: false,
            isLandscapeSidebar: false,
            isContentReady: false
        }
    },
    computed: {
        ContentTypes() {
            return ContentTypes;
        },
        orientationTypes() {
            return OrientationTypes;
        },
        currentContent() {
            return this.$store.state.contentCreatorModule.currentContent;
        },
        facility() {
            return this.$store.state.facilityModule.selectedFacility;
        },
        isNewsletter() {
            return this.currentContent?.type === ContentTypes.PrintableNewsletter;
        },
        wings() {
            return [...[{ name: this.$t('Calendar.AllWings'), value: "All", type: EventLocations.Wing}], ...this.$store.state.eventLocationModule.eventWings];
        },
        currentFacility(): string {
            return this.$store.state.authModule.currentFacility;
        },
        isCanvas() {
            return (this.currentContent?.hasOwnProperty('contentCreatorType') && this.currentContent.contentCreatorType === ContentCreatorTypes.FreeDesign) ? true : false;
        }
    },
    async created() {
        await this.$store.dispatch("getCurrentCreatorContent", this.$route.params?.id);
        await this.$store.dispatch("getEventWings")
        this.isContentReady = true;
        this.$store.dispatch("getContentTags")
        this.setContentOrientation();
        this.$emit('showSidebar', false);
    },
    watch: {
        builderData(newValue) {
            this.checkContentHtml(newValue)
        },
        selectedOrientation(newValue) {
            if(newValue === this.orientationTypes.Landscape) {
                this.isLandscape = true;
            } else {
                this.isLandscape = false;
            }

            if(newValue === this.orientationTypes.LandscapeSidebar) {
                this.isLandscapeSidebar = true;
            } else  {
                this.isLandscapeSidebar = false;
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        this.$emit('showSidebar', true);
        this.builder.destroy();

        const linkTags = document.head.querySelectorAll("link");
        linkTags.forEach(linkTag => {
            if (linkTag.getAttribute("href").includes("demo.innovastudio")) {
                linkTag.remove();
            }
        });

        next();
    },
    methods: {
        routeToContentList() {
            this.$router.push("/content-list");
        },
        async saveContent() {
            this.loading.save = true;

            delay(1000).then(async () => {
                await this.modifyPhotosBase64(this.builderData.html);

                const data = {
                    id: this.$route.params?.id,
                    data: {
                        selectedWing: this.selectedWing?.id,
                        orientation: this.selectedOrientation,
                        ...this.builderData
                    }
                };

                await this.$store.dispatch("saveCreatorContent", data).then(async () => {
                    this.$toast.open({
                        message: this.$i18n.messages[this.$i18n.locale]['ContentView.SavedMessage'],
                        type: "success"
                    });

                    this.isDisablingSaving = true;

                    await this.$store.dispatch("getCurrentCreatorContent", this.$route.params?.id);
                }).catch((error) => {
                    this.$toast.open({
                        message: `${error.message}`,
                        type: "error"
                    });
                });

                this.loading.save = false;
            });
        },
        async publishContent() {
            if(this.currentContent?.type === this.ContentTypes.DigitalSignagePage) {
                this.$refs.ContentCreatorPublishDlg.open(this.currentContent)
                    .then(async response => {
                        if (response && response.agree) {
                            await this.publishCreatorContent();
                        }
                    })
            } else {
                await this.publishCreatorContent();
            }
        },
        async publishCreatorContent() {
            this.loading.publish = true;

            const data = {
                id: this.$route.params?.id,
                data: {
                    status: ContentStatus.Published,
                    datePublished: moment(new Date()).toDate(),
                    selectedWing: this.selectedWing?.id,
                    ...this.builderData,
                }
            };

            await this.$store.dispatch("publishCreatorContent", data).then(() => {
                this.$toast.open({
                    message: this.$i18n.messages[this.$i18n.locale]['ContentView.PublishedMessage'],
                    type: "success"
                });
            }).catch((error) => {
                this.$toast.open({
                    message: `${error.message}`,
                    type: "error"
                });
            });

            this.loading.publish = false;
        },
        async deleteContent() {
            const contentId = this.$route.params?.id;
            this.loading.delete = true;

            (this.$refs.confirmationModal as any)
              .open(null, this.$i18n.messages[this.$i18n.locale]['ContentView.DeleteMessage'], false)
              .then(async response => {
                  if (response && response.agree) {
                      await this.$store.dispatch("deleteCreatorContent", contentId).then(async () => {
                          await this.$store.dispatch("deleteContentFromDigitalSignage", contentId);
                          this.$router.push("/content-list");
                          this.$toast.open({
                              message: this.$i18n.messages[this.$i18n.locale]['ContentView.DeletedMessage'],
                              type: "success"
                          });
                      }).catch((error) => {
                          this.$toast.open({
                              message: `${error.message}`,
                              type: "error"
                          });
                          this.loading.delete = false;
                      });
                  } else {
                      this.loading.delete = false;
                  }
              });
        },
        async exportContent() {
            this.loading.export = true;

            const contentString = this.currentContent.html;

            const Handlebars = require('handlebars');
            const compiledTemplate = Handlebars.compile(contentString);
            await this.getData(contentString);

            const generatedHtml = compiledTemplate(this.dataCustomContent);

            this.contentPDF = await this.replaceBackgroundImageWithBase64(generatedHtml) + `${this.currentContent.sectionCss}`;

            setTimeout(async () => {
                const canvas = await html2canvas(document.querySelector('#content-pdf'));
                this.contentScreenshot = canvas.toDataURL();

                (this.$refs as any).html2Pdf.generatePdf();
                setTimeout(() => {
                    this.dataCustomContent.events = [];
                    this.dataCustomContent.menu = [];
                    this.dataCustomContent.breakfast = [];
                    this.dataCustomContent.lunch = [];
                    this.dataCustomContent.dinner = [];
                    this.dataCustomContent.dates = {};
                    this.dataCustomContent.dailyCalendar = {};
                    this.dataCustomContent.weeklyCalendar = {};
                    this.dataCustomContent.monthlyCalendar = [];
                    this.dataCustomContent.weeksBirthday = [];
                    this.contentPDF = null;
                    this.contentScreenshot = "";
                    this.contentScreenshotResult = "";
                    this.loading.export = false;
                }, 1000)
            }, 500);
        },
        async replaceBackgroundImageWithBase64(generatedHtml) {
            const tempElement = document.createElement('div');
            tempElement.innerHTML = generatedHtml;

            const elementsWithBackgroundImage: any = tempElement.querySelectorAll('[style*="background-image"]');

            for (let i = 0; i < elementsWithBackgroundImage.length; i++) {
                let backgroundImage = elementsWithBackgroundImage[i].style.backgroundImage;

                if(backgroundImage.includes("no-image")) {
                    backgroundImage = `url("${DEFAULT_NO_IMAGE}")`;
                }

                const url = backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/i)[1];
                const base64Data = await imageToBase64(url);
                elementsWithBackgroundImage[i].style.backgroundImage = `url(data:image/png;base64,${base64Data})`;
            }

            const elementsWithImages: any = tempElement.querySelectorAll('img[src]');

            for (let i = 0; i < elementsWithImages.length; i++) {
                let url = elementsWithImages[i].src;

                if(url.includes("no-image")) {
                    url = DEFAULT_NO_IMAGE;
                }

                const base64Data = await imageToBase64(url);
                elementsWithImages[i].src = "data:image/png;base64," + base64Data;
            }

            return tempElement.outerHTML;
        },
        makeCopy() {
            const data = this.builderData?.html ? this.builderData : {html: this.currentContent.html, mainCss: this.currentContent.mainCss, sectionCss: this.currentContent.sectionCss };
            this.$refs.ContentCreatorDlg.open(this.$t('ContentCreatorDlg.CopyContent'), data);
        },
        addTags() {
            this.$refs.ContentTagsDlg.open(this.currentContent);
        },
        openPreview() {
            const route = this.$router.resolve({
                path:
                  "/content-list/" +
                  this.$store.state.authModule.currentFacility +
                  "/v/" +
                  this.$route.params?.id
            });
            window.open(route.href, "_blank");
        },
        getBuilder(builder) {
            this.builder = builder;
        },
        getBuilderHtml(data) {
            this.loading.save = false;
            this.isDisablingSaving = false;
            this.builderData = data;
        },
        isBuilderReady() {
            this.loading.page = false;
            this.checkContentHtml(this.currentContent);
            this.checkAlreadySelectedWing();
        },
        disableSaving() {
            this.loading.save = true;
            this.isDisablingSaving = true;
        },
        selectWing(wing) {
            if(this.currentContent && this.currentContent?.selectedWing !== wing?.id) {
                this.isDisablingSaving = false;
            }

            this.selectedWing.name = wing?.name || '';
            this.selectedWing.id = wing?.id ?? this.$t('GeneralTranslations.All');
        },
        selectType(value) {
            if(this.currentContent && this.currentContent.orientation !== value) {
                this.isDisablingSaving = false;
            }

            this.selectedOrientation = value;
        },
        checkContentHtml(value) {
            this.isShowWings = !!(value?.html?.includes('id="Upcoming Events"') ||
                value?.html?.includes('id="MonthlyCalendar"') ||
                value?.html?.includes('id="WeeklyCalendar"') ||
                value?.html?.includes('id="DailyCalendar"'));
        },
        checkAlreadySelectedWing() {
            if(this.currentContent?.selectedWing) {
                const wing = this.wings.find(x => x.id === this.currentContent?.selectedWing);
                this.selectWing(wing)
            }
        },
        async modifyPhotosBase64(html) {
            let htmlStr = html;

            if(htmlStr === undefined) {
                htmlStr = this.currentContent.html
            } else {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const imagesWithBase64Src: any = doc.querySelectorAll('img[src^="data:"]');

                for(let i = 0; i < imagesWithBase64Src.length; i++) {
                    const base64Data = imagesWithBase64Src[i].src.split(',')[1];
                    const base64Type = imagesWithBase64Src[i].src.split(";")[0].split(":")[1];
                    const fileName = uuidv4();

                    const base64ToFile = this.base64ToFile(base64Data, fileName, base64Type);
                    const photoURL = await this.uploadToStorage(base64ToFile);
                    htmlStr = htmlStr.replace(imagesWithBase64Src[i].src, photoURL)
                }
            }

            this.builderData.html = htmlStr;
        },
        base64ToFile(base64String, fileName, contentType = '') {
            const binaryString = atob(base64String);
            const arrayBuffer = new ArrayBuffer(binaryString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < binaryString.length; i++) {
                uint8Array[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([uint8Array], { type: contentType });
            return new File([blob], fileName, { type: contentType });
        },
        async uploadToStorage(base64ToFile) {
            const file: any = await optimizePhotos(base64ToFile, 'content-image');
            const path = `contentbox/assets/${this.currentFacility}/${uuidv4()}`;

             return await uploadImageAsync({
                path,
                file,
             }).then(async (storage) => {
                 return await storage.ref.getDownloadURL();
             })
        },
        setContentOrientation() {
            if (this.currentContent?.hasOwnProperty('orientation')) {
                this.selectedOrientation = this.currentContent.orientation;
            } else {
                this.selectedOrientation = this.currentContent?.type === this.ContentTypes.DigitalSignagePage
                  ? this.orientationTypes.Landscape
                  : this.orientationTypes.Portrait;
            }

            this.isLandscape = this.selectedOrientation === this.orientationTypes.Landscape;
            this.isLandscapeSidebar = this.selectedOrientation === this.orientationTypes.LandscapeSidebar;
        },
        editContentName() {
            this.$refs.ContentEditDlg.open(this.currentContent.name);
        },
    }
}
